import * as React from 'react';
import { AuftragFilter, useAuftragList } from 'api/auftrag';
import { AuftragPositionFilter, useAuftragPositionList } from 'api/auftragPosition';
import { LieferungFilter, useLieferungList } from 'api/lieferung';
import { getPercent } from 'reports/utils';
import { defaultLineData, FilterValues, LineData, OffeneAuftraegeSummary } from '../types';

export const useOffeneAuftraegeSummary = (filterValues: FilterValues): OffeneAuftraegeSummary => {
    const auftragListFilter = React.useMemo<AuftragFilter>(
        () => ({
            betreuerExact: filterValues.betreuer,
            telBetreuerExact: filterValues.telBetreuer,
            status: filterValues.status,
            rahmen: false,
            onlyOpen: filterValues.berechnungsart,
            withKunde: true,
        }),
        [filterValues.betreuer, filterValues.telBetreuer, filterValues.status, filterValues.berechnungsart],
    );

    const auftragList = useAuftragList(auftragListFilter);

    const auftragPositionListFilter = React.useMemo<AuftragPositionFilter>(
        () => ({
            auNrList: (auftragList.data || []).map((auftrag) => auftrag.nr),
        }),
        [auftragList.data],
    );

    const auftragPositionList = useAuftragPositionList(auftragPositionListFilter);

    const lieferungListFilter = React.useMemo<LieferungFilter>(
        () => ({
            auNrList: (auftragList.data || []).map((auftrag) => auftrag.nr),
        }),
        [auftragList.data],
    );

    const lieferungList = useLieferungList(lieferungListFilter);

    return React.useMemo<OffeneAuftraegeSummary>(() => {
        const total: LineData = { ...defaultLineData };
        const betreuerTotal: { [key: number]: LineData } = {};
        const telBetreuerTotal: { [key: number]: LineData } = {};

        const lineDataList = (auftragList.data || []).map((auftrag) => {
            const auftragPositionen = (auftragPositionList.data || []).filter(
                (position) => position.auNr === auftrag.nr,
            );

            const lieferungen = (lieferungList.data || [])
                .filter((lieferung) => lieferung.auNr === auftrag.nr)
                .sort((a, b) => a.datum.localeCompare(b.datum));

            let ekNettoGesamt = 0;
            let ekNettoOffen = 0;
            let vkNettoGesamt = 0;
            let vkNettoOffen = 0;
            auftragPositionen.forEach((auftragPosition) => {
                ekNettoGesamt += auftragPosition.posEkPreis * auftragPosition.posMenge;
                vkNettoGesamt += auftragPosition.posEPreis * auftragPosition.posMenge;
                ekNettoOffen += auftragPosition.posEkPreis * auftragPosition.offen;
                vkNettoOffen += auftragPosition.posEPreis * auftragPosition.offen;
            });

            const bMargeGesamt = vkNettoGesamt - ekNettoGesamt;

            const bMargeOffen = vkNettoOffen - ekNettoOffen;

            const ekNettoOffenP = getPercent(ekNettoGesamt, ekNettoOffen);
            const vkNettoOffenP = getPercent(vkNettoGesamt, vkNettoOffen);
            const bMargeOffenP = getPercent(bMargeGesamt, bMargeOffen);

            total.count += 1;
            total.ekNettoGesamt += ekNettoGesamt;
            total.ekNettoOffen += ekNettoOffen;
            total.ekNettoOffenP = getPercent(total.ekNettoGesamt, total.ekNettoOffen);
            total.vkNettoGesamt += vkNettoGesamt;
            total.vkNettoOffen += vkNettoOffen;
            total.vkNettoOffenP = getPercent(total.vkNettoGesamt, total.vkNettoOffen);
            total.bMargeGesamt += bMargeGesamt;
            total.bMargeOffen += bMargeOffen;
            total.bMargeOffenP = getPercent(total.bMargeGesamt, total.bMargeOffen);
            total.anzahlLieferungen += lieferungen.length;

            if (auftrag.betreuer) {
                if (!betreuerTotal[auftrag.betreuer]) {
                    betreuerTotal[auftrag.betreuer] = { ...defaultLineData };
                }

                betreuerTotal[auftrag.betreuer].count += 1;

                betreuerTotal[auftrag.betreuer].ekNettoGesamt += ekNettoGesamt;
                betreuerTotal[auftrag.betreuer].ekNettoOffen += ekNettoOffen;
                betreuerTotal[auftrag.betreuer].ekNettoOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].ekNettoGesamt,
                    betreuerTotal[auftrag.betreuer].ekNettoOffen,
                );
                betreuerTotal[auftrag.betreuer].ekNettoOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].ekNettoGesamt,
                    betreuerTotal[auftrag.betreuer].ekNettoOffen,
                );
                betreuerTotal[auftrag.betreuer].vkNettoGesamt += vkNettoGesamt;
                betreuerTotal[auftrag.betreuer].vkNettoOffen += vkNettoOffen;
                betreuerTotal[auftrag.betreuer].vkNettoOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].vkNettoGesamt,
                    betreuerTotal[auftrag.betreuer].vkNettoOffen,
                );
                betreuerTotal[auftrag.betreuer].vkNettoOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].vkNettoGesamt,
                    betreuerTotal[auftrag.betreuer].vkNettoOffen,
                );
                betreuerTotal[auftrag.betreuer].bMargeGesamt += bMargeGesamt;
                betreuerTotal[auftrag.betreuer].bMargeOffen += bMargeOffen;
                betreuerTotal[auftrag.betreuer].bMargeOffenP = getPercent(
                    betreuerTotal[auftrag.betreuer].bMargeGesamt,
                    betreuerTotal[auftrag.betreuer].bMargeOffen,
                );
                betreuerTotal[auftrag.betreuer].anzahlLieferungen += lieferungen.length;
            }

            if (auftrag.telBetreuer) {
                if (!telBetreuerTotal[auftrag.telBetreuer]) {
                    telBetreuerTotal[auftrag.telBetreuer] = { ...defaultLineData };
                }

                telBetreuerTotal[auftrag.telBetreuer].count += 1;

                telBetreuerTotal[auftrag.telBetreuer].ekNettoGesamt += ekNettoGesamt;
                telBetreuerTotal[auftrag.telBetreuer].ekNettoOffen += ekNettoOffen;
                telBetreuerTotal[auftrag.telBetreuer].ekNettoOffen += ekNettoOffen;
                telBetreuerTotal[auftrag.telBetreuer].ekNettoOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoGesamt,
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].ekNettoOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoGesamt,
                    telBetreuerTotal[auftrag.telBetreuer].ekNettoOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].vkNettoGesamt += vkNettoGesamt;
                telBetreuerTotal[auftrag.telBetreuer].vkNettoOffen += vkNettoOffen;
                telBetreuerTotal[auftrag.telBetreuer].vkNettoOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoGesamt,
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].vkNettoOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoGesamt,
                    telBetreuerTotal[auftrag.telBetreuer].vkNettoOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].bMargeGesamt += bMargeGesamt;
                telBetreuerTotal[auftrag.telBetreuer].bMargeOffen += bMargeOffen;
                telBetreuerTotal[auftrag.telBetreuer].bMargeOffenP = getPercent(
                    telBetreuerTotal[auftrag.telBetreuer].bMargeGesamt,
                    telBetreuerTotal[auftrag.telBetreuer].bMargeOffen,
                );
                telBetreuerTotal[auftrag.telBetreuer].anzahlLieferungen += lieferungen.length;
            }

            return {
                auNr: auftrag.nr,
                status: auftrag.status,
                datum: auftrag.datum,
                datBis: auftrag.datBis,
                kNr: auftrag.kNr || 0,
                text: auftrag.kunde?.name1 || '?',
                count: 0,
                ekNettoGesamt,
                ekNettoOffen,
                ekNettoOffenP,
                vkNettoGesamt,
                vkNettoOffen,
                vkNettoOffenP,
                bMargeGesamt,
                bMargeOffen,
                bMargeOffenP,
                anzahlLieferungen: lieferungen.length,
                letzteLieferung: lieferungen.length > 0 ? lieferungen[lieferungen.length - 1].datum : '',
            };
        });

        return {
            loading: auftragList.isLoading || auftragPositionList.isLoading || lieferungList.isLoading,
            total,
            lineDataList,
            betreuerTotal,
            telBetreuerTotal,
        };
    }, [
        auftragList.data,
        auftragList.isLoading,
        auftragPositionList.isLoading,
        auftragPositionList.data,
        lieferungList.isLoading,
        lieferungList.data,
    ]);
};
